import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Mail, Phone, MapPin } from 'lucide-react';
import Logo from './Logo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    company: [
      { name: 'About', path: '/about' },
      { name: 'Services', path: '/services' },
      { name: 'Contact', path: '/contact' },
    ],
    platform: [
      { name: 'Videra Platform', path: '/videra' },
      { name: 'Features', path: '/videra#features' },
      { name: 'Pricing', path: '/videra#pricing' },
      { name: 'Try Demo', path: 'https://demo.videra.app', external: true },
    ],
  };

  return (
    <footer className="bg-slate-50 border-t border-slate-200" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Logo size="default" />
            <p className="mt-6 text-slate-600 text-sm leading-relaxed">
              Two decades of frontline delivery experience. 
              Transforming how organisations manage complex project portfolios.
            </p>
            <div className="mt-6 flex items-center gap-4">
              <a 
                href="https://www.linkedin.com/in/peter-fulford-aa03467/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="p-2 bg-white border border-slate-200 hover:border-slate-900 transition-colors"
                data-testid="footer-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-4 h-4 text-slate-700" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              Company
            </h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-600 hover:text-slate-900 text-sm transition-colors"
                    data-testid={`footer-link-${link.name.toLowerCase()}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Platform Links */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              Platform
            </h4>
            <ul className="space-y-4">
              {footerLinks.platform.map((link) => (
                <li key={link.path}>
                  {link.external ? (
                    <a
                      href={link.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-600 hover:text-slate-900 text-sm transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link
                      to={link.path}
                      className="text-slate-600 hover:text-slate-900 text-sm transition-colors"
                      data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <a 
                  href="mailto:hello@keystoneconsulting.uk" 
                  className="text-slate-600 hover:text-slate-900 text-sm transition-colors"
                  data-testid="footer-email"
                >
                  hello@keystoneconsulting.uk
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600 text-sm">+44(0)7703 577261</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                <span className="text-slate-600 text-sm">United Kingdom</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            © {currentYear} Keystone Consultants Limited. All rights reserved.
          </p>
          <p className="text-slate-500 text-sm">
            Strategic Project Delivery. Digitised.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
